import React, {useEffect, useLayoutEffect, useState} from 'react';
import {Col, Container, Row} from 'reactstrap';
import ABTest, {DebugMenu} from 'react-controlled-ab';
import {subscribeToCampaign} from 'react-controlled-ab/datalayers/vwo';
import classNames from 'classnames';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faCartPlus} from '@fortawesome/pro-light-svg-icons/faCartPlus';
import {usePhaseState, usePhaseDispatch} from 'funnel-schedule/phase-context';
import queryString from 'query-string';
import PageMeta from 'components/PageMeta';
import Section, {SectionImage} from 'components/Elements/Section';
import HeaderVideo from 'components/Elements/HeaderVideo';
import CheckoutButton from 'components/CheckoutButton';
import {SalesCta497, DynamicSalesCta, SalesCta297, SalesDisclaimer} from 'components/SalesCta';
import Footer from 'components/Elements/Footer';
import {useUserState} from 'contexts/user';
import ModalExitSales from 'components/Modals/ModalExitSales';
import SalesCopy from 'components/SalesCopy';
import track from 'utils/track';
import CountdownTimer from 'components/Elements/CountdownTimer';
import useBalanceText from 'utils/useBalanceText';
import useBeacon from 'utils/useBeacon';

export default function SalesIndex({
  noExitIntent = false,
  noFixedVideo = true,
  ctaOverride = undefined,
  videoUrlOverride = '',
  ctaNoTimer = false,
  variant = null,
  version = undefined,
  ...props
}) {
  const {bucket, email, registrationId} = useUserState();
  const {
    phases: {
      default: defaultSchedule = {
        phase: 'before',
        launchStart: '',
        launchEnd: '',
        launchTitle: ''
      },
      sales: salesSchedule = {
        phase: 'during',
        salesEnd: '',
        isSalesDay: false
      },
      webinar: webinarSchedule = {
        phase: 'before'
      }
    }
  } = usePhaseState();

  const pageTitle = 'Join Now and Take Control of Your Heart Health';
  const pageDescription =
    'Healthy Heart is a breakthrough online course taught by renowned holistic cardiologist Dr. Mimi Guarneri. Get exclusive access to Dr. Mimi’s latest breakthrough research, Health Coaches and Certified Nutritionists, plant-powered recipes, a supportive community, and more!';
  const ogImage = 'https://cdn.foodrevolution.org/hhc/opengraph/hhc-open-graph-202107-sales-v.jpg';
  const shareUrl = 'https://heart.foodrevolution.org/join/';

  const qs = typeof window !== 'undefined' ? queryString.parse(window.location.search) : {};
  const {t} = qs.t ? qs : {};

  const isSalesDay = new Date(webinarSchedule.endOfSalesDay * 1000 - 86400000).getTime() < new Date().getTime();

  const salesFullPrice =
    (!email && !registrationId && !ctaOverride) ||
    (!ctaOverride && webinarSchedule.phase === 'after') ||
    ctaOverride === 'sales497';

  const specialTimer = version === 'salesSpecial' && salesSchedule.phase === 'during';
  const salesEnd = new Date(salesSchedule.salesEnd).getTime();
  const endOfSalesDay = new Date(webinarSchedule.endOfSalesDay * 1000).getTime();

  const timerEnd = specialTimer ? salesEnd : endOfSalesDay;

  const productId = 'C-HHC';
  const productSku = 'C-HHC';
  const productName = 'Healthy Heart';
  const productSlugFull = 'hhc';
  const productSlugSales = 'hhc-special';
  const productSlug = t || !salesFullPrice ? productSlugSales : productSlugFull;
  const productValue = t || !salesFullPrice ? 297 : 497;
  const productPaymentPlanValue = !salesFullPrice ? 109 : 179;
  const productFullPrice = 497;

  useEffect(() => {
    track('Product Viewed', {
      product_id: productId,
      sku: productSku,
      name: productName,
      variant: productSlug,
      value: productValue,
      currency: 'usd'
    });
  }, [salesFullPrice]);

  function SalesCta({headline, btnText = 'Buy Now', className = undefined, noImage = false, trees = true}) {
    return (
      <div className={classNames(`cta-sales`, className)}>
        {headline && <div className="cta-headline text-center text-white">{headline}</div>}
        <div
          className={classNames(
            `offer-box`,
            `offer-box-shadow`,
            `px-3`,
            `py-4`,
            `px-sm-4`,
            `px-md-5`,
            `py-md-5`,
            `text-center`,
            `background-white`,
            `rounded`
          )}
        >
          <h2 className="section-heading">
            Get lifetime access to the <i>Healthy Heart Course</i> today!
          </h2>
          {!noImage && (
            <Row className="justify-content-center mb-4">
              <Col xs="10" md="8" xl="7">
                <img
                  src="https://cdn.foodrevolution.org/hhc/hhc-product-image-v2b.png"
                  width="1200"
                  height="667"
                  alt="Healthy Heart product graphic"
                />
              </Col>
            </Row>
          )}
          {!salesFullPrice ? (
            <>
              <h3 className="mt-0 mb-2">
                Regular Price: <del>${productFullPrice}</del>
              </h3>
              <h2 className="text-green mt-0">Right Now Only: ${productValue}</h2>
            </>
          ) : (
            <>
              <h2 className="text-h1 mt-0 mb-2">
                Your Price: <span className="text-green">${productFullPrice}</span>
              </h2>
            </>
          )}
          {!salesFullPrice && !ctaNoTimer ? (
            <div className="my-3">
              <h5 className="section-heading mb-2">Your discount expires in:</h5>
              <div className="countdown-dark">
                <CountdownTimer time={timerEnd} />
              </div>
            </div>
          ) : null}
          <CheckoutButton className="offer-box-button" sku={productSku} price={productValue} slug={productSlug}>
            <FontAwesomeIcon className="mr-2" icon={faCartPlus} /> {btnText}
          </CheckoutButton>
          <SalesDisclaimer />
          <p className="mt-4 mb-1">Prefer a 3-month payment plan? We’ve got you covered!</p>
          <CheckoutButton
            color="link"
            className="btn-link-checkout"
            sku={productSku}
            price={productPaymentPlanValue}
            slug={productSlug}
            queryParams={{opt: 1}}
          >
            Join now for just ${productPaymentPlanValue}/month!
          </CheckoutButton>
        </div>

        {trees && (
          <Row className="trees-for-future text-white d-flex align-items-center justify-content-center mt-5">
            <Col xs="11" lg="8" className="mb-4 mb-lg-0">
              <h6 className="section-only-heading text-center text-lg-left text-400">
                For every new order, we make a donation to Trees for the Future, enabling them to plant another organic
                fruit or nut tree in a low-income community.
              </h6>
            </Col>
            <Col xs="11" lg="3" className="text-center">
              <img
                alt="Trees for the Future logo"
                src="https://cdn.foodrevolution.org/global/trees-logo-white.png"
                width={1080}
                height={442}
                style={{maxHeight: '100px', width: 'auto'}}
              />
            </Col>
          </Row>
        )}
      </div>
    );
  }

  useBalanceText();
  useBeacon();
  return (
    <div className="page page-nosocial d-flex flex-column">
      <PageMeta title={pageTitle} description={pageDescription} url={shareUrl} ogImage={ogImage} />

      <div className="page-content">
        <Section id="hbc-header" className="section-angle-first" innerClass="text-white text-center" color="black">
          <Container>
            <Row>
              <Col>
                <h2 className="section-heading">
                  {variant === 'ad' ? (
                    <>Want To Know How To Maximize Heart Health?</>
                  ) : (
                    <>
                      {
                        {
                          prevent: <>Do You Know How To Stop Heart Disease From Stopping You?</>,
                          optimize: <>Do You Know How To Have The Strongest Heart Possible?</>,
                          reverse: <>Do You Know How To Stop Heart Disease From Stopping You?</>
                        }[bucket]
                      }
                    </>
                  )}
                </h2>
              </Col>
            </Row>
            <Row className="d-flex justify-content-center">
              <Col lg="10" className="video-col px-0 px-sm-3">
                <h5 className="section-heading">Watch this video to learn the truth:</h5>
                <HeaderVideo
                  bgColor="black"
                  className="text-white"
                  sku="hbc"
                  price={productValue}
                  slug={productSlug}
                  url={
                    videoUrlOverride
                      ? videoUrlOverride
                      : t || !salesFullPrice
                      ? isSalesDay
                        ? {
                            prevent: 'https://www.youtube.com/watch?v=RphPKMRHgWE',
                            optimize: 'https://www.youtube.com/watch?v=FyVODU1C2JA',
                            reverse: 'https://www.youtube.com/watch?v=XFsNHLBNqLY'
                          }[bucket]
                        : {
                            prevent: 'https://www.youtube.com/watch?v=aJ4gcghNFdQ',
                            optimize: 'https://www.youtube.com/watch?v=UIGtnq9ccmU',
                            reverse: 'https://www.youtube.com/watch?v=WvzScxR9wfA'
                          }[bucket]
                      : {
                          prevent: 'https://www.youtube.com/watch?v=a538-bNh_n4',
                          optimize: 'https://www.youtube.com/watch?v=F0ecIoLkORE',
                          reverse: 'https://www.youtube.com/watch?v=kH3TQG6ilwk'
                        }[bucket]
                  }
                  sales
                  heading="Eat, Move, Sleep, Think, & Play for Lifelong Brain Health"
                  btnText="Join Healthy Brain Now"
                  noFixed={noFixedVideo}
                  label="HHC - Sales Video - Join"
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <h4 className="mb-0">
                  {variant === 'ad' ? (
                    <>Get Supercharged Heart Health</>
                  ) : (
                    <>Give your heart the love it needs using all-natural solutions that don’t cost a dime.</>
                  )}
                </h4>
                <p className="my-4">
                  <img className="img-course-logo" src="https://cdn.foodrevolution.org/hhc/hhc-logo-white.svg" />
                </p>
                <h4 className="mt-0">
                  With Dr. Mimi Guarneri and Your Host, Ocean Robbins (CEO of Food Revolution Network)
                </h4>
                <p>Get immediate access to the groundbreaking program!</p>
                <div className="header-cta-wrap mt-5">
                  {t || !salesFullPrice ? (
                    <>
                      <h2 className="section-heading mb-3">
                        Right Now Just <del>$497</del> $297
                      </h2>
                      {ctaOverride === 'sales297' ? (
                        <>
                          {ctaNoTimer ? null : (
                            <>
                              <div className="cta-timer mb-3">
                                <div className="offer-box-timer-text">Your discount expires in:</div>
                                <CountdownTimer time={timerEnd} />
                              </div>
                            </>
                          )}
                        </>
                      ) : (
                        <>
                          <div className="cta-timer mb-3">
                            <div className="offer-box-timer-text">Your discount expires in:</div>
                            <CountdownTimer time={timerEnd} />
                          </div>
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      <h2 className="section-heading">Right Now $497</h2>
                    </>
                  )}
                  <CheckoutButton sku={productSku} price={productValue} slug={productSlug} />
                  <SalesDisclaimer iconColor="white" />
                </div>
              </Col>
            </Row>
          </Container>
        </Section>

        <SalesCopy version="sales" variant={variant}>
          <SalesCta
            headline={
              <>
                <h2 className="section-heading mb-3">Ready Now? Join Us!</h2>
                <p className="">Unlimited Access. Enjoy it all as much as you want.</p>
              </>
            }
          />
        </SalesCopy>
      </div>

      {noExitIntent ? null : (
        <ModalExitSales version={t || !salesFullPrice ? (isSalesDay ? 'salesDay' : 'sales297') : 'sales497'} />
      )}

      <div className="footer-content mt-auto">
        <Footer />
      </div>
    </div>
  );
}
